import React from 'react';

import {useGraphQLFieldSelectorContext} from './GraphQLFieldSelectorContext';
import {GraphQLField} from './GraphQLFieldSelectorTypes';
import Row from './Row';

/** @package */
export interface FieldRowProps {
  /**
   * If provided, render to the right of the field name. Overflow will be ellipsized.
   */
  description?: string;

  /**
   * If `null`, no expand icon will be rendered but the space (20px) is reserved.
   */
  expandIcon: JSX.Element | null;

  field: GraphQLField;
  indentationLevel: number;

  /**
   * If `null`, no checkbox will be rendered but the space (16px) is reserved.
   */
  checkbox: JSX.Element | null;

  onClick: () => void;
}

/**
 * Render a row for a field in the `GraphQLFieldSelector` component.
 *
 * `Render` `|Checkbox|...1|Expand Icon|Type Icon|Field Name|...2|`, where:
 * - `|...1|` is the dynamic spacing based on `indentationLevel` prop
 * - `|...2|` is the remaining space in the row
 *
 * @package
 */
export default function FieldRow({
  description,
  field,
  indentationLevel,
  onClick,
  checkbox,
  expandIcon,
}: FieldRowProps): JSX.Element {
  const {renderFieldName, renderFieldTypeIcon} = useGraphQLFieldSelectorContext();
  return (
    <Row
      checkbox={checkbox}
      description={description}
      expandIcon={expandIcon}
      typeIcon={renderFieldTypeIcon && renderFieldTypeIcon(field)}
      indentationLevel={indentationLevel}
      text={renderFieldName(field) ?? field.name}
      onClick={onClick}
    />
  );
}
